/**
 * SMSReportEngine
 * Version 2.4.0
 *
 * Phase 1:
 * - Extracted Farm Management Report from Shopify UI
 * - Added reusable report data model
 * - Preserved existing PDF report functionality
 *
 * Version 2.2:
 * - Added a self-contained pdfMake dependency loader
 * - Added ordered, duplicate-safe font-library loading
 * - Added shared dependency promises for concurrent requests
 * - Removed the requirement for Shopify UI dependency scripts
 *
 * Version 2.2.1:
 * - Replaced CDN dependencies with configured Shopify vendor assets
 * - Added legacy and modern pdfMake font registration support
 * - Added font-registration smoke testing without relying on pdfMake.vfs
 * - Cached dependency URLs supplied through init()
 *
 * Version 2.2.2:
 * - Added automatic Shopify sibling-asset discovery
 * - Removed the need to pass vendor asset URLs to init()
 * - Retained optional URL overrides for testing and non-standard setups
 *
 * Version 2.3:
 * - Integrated Seasonal Summary as the source of truth for plan progress
 * - Integrated Seasonal Engine for saved seasonal farm state
 * - Added current-year and latest-plan resolution
 * - Added defensive raw-storage fallback
 * - Removed duplicated primary seasonal progress calculations
 *
 * Version 2.4.0:
 * - Uses SMSSeasonalEngine.getPlan() as the only seasonal-plan source
 * - Uses SMSSeasonalSummary as the only progress and completion authority
 * - Normalises plan.tasks through season-specific report adapters
 * - Keeps task IDs inside SeasonalReportAdapters
 * - Treats My Block important paddocks as fallback values only
 * - Supports future Summer and Autumn controllers without farm-state logic
 */
window.SMSReportEngine = (() => {
  "use strict";

  const VERSION = "2.4.0";
  const LOG_PREFIX = "[SMSReportEngine]";
  const REPORT_TYPE = "farm-management";
  const MAP_TYPES = Object.freeze(["overall", "winter", "spring", "summer", "autumn"]);
  const SEASONS = Object.freeze(["winter", "spring", "summer", "autumn"]);
  const MAX_DECISION_HISTORY = 20;

  const DEPENDENCY_TIMEOUT_MS = 30000;
  const FONT_VALIDATION_TIMEOUT_MS = 8000;
  const PDFMAKE_ASSET_NAME = "vendor-pdfmake.min.js";
  const PDF_FONTS_ASSET_NAME = "vendor-pdfmake-fonts.js";
  const ENGINE_ASSET_NAME = "sms-report-engine.js";
  const ENGINE_SCRIPT_URL = (() => {
    const current = document.currentScript;

    if (current && current.src) {
      return current.src;
    }

    const scripts = Array.from(document.scripts || []);
    const matchingScript = scripts
      .slice()
      .reverse()
      .find(script => {
        const source = String(script.src || "");
        return (
          source.includes(`/${ENGINE_ASSET_NAME}`) ||
          source.includes("/sms-report-engine-v")
        );
      });

    return matchingScript?.src || "";
  })();

  const REPORT_THEME = Object.freeze({
    colours: Object.freeze({
      green: "#1F5A46",
      dark: "#214838",
      body: "#34453D",
      muted: "#68756F",
      pale: "#EEF4EF",
      cream: "#F6F4EF",
      blue: "#EDF5F7",
      line: "#D9E4DD",
      white: "#FFFFFF",
      red: "#A7473E"
    }),
    page: Object.freeze({
      size: "A4",
      margins: Object.freeze([42, 44, 42, 46])
    })
  });

  let initialisationPromise = null;
  let ready = false;
  let generationPromise = null;
  let dependencyPromise = null;
  let pdfDependenciesReady = false;
  let modernFontsRegistered = false;
  let fontValidationPromise = null;
  const scriptPromises = new Map();
  let engineOptions = {};

  function log(...args) {
    console.log(LOG_PREFIX, ...args);
  }

  function warn(...args) {
    console.warn(LOG_PREFIX, ...args);
  }

  function isObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
  }

  function clone(value) {
    if (value === undefined) return undefined;
    if (typeof structuredClone === "function") return structuredClone(value);
    return JSON.parse(JSON.stringify(value));
  }

  function valueOr(value, fallback) {
    return value === undefined || value === null || value === "" ? fallback : value;
  }

  function numberOr(value, fallback = 0) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function clamp(value, minimum, maximum) {
    return Math.min(maximum, Math.max(minimum, value));
  }

  function humanise(value, fallback = "Not recorded") {
    if (value === undefined || value === null || value === "") return fallback;
    return String(value)
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, letter => letter.toUpperCase());
  }

  function safeText(value, fallback = "Not recorded") {
    if (value === undefined || value === null || value === "") return fallback;
    return String(value);
  }

  function formatNumber(value, options = {}) {
    const number = Number(value);
    if (!Number.isFinite(number)) return options.fallback || "Not recorded";
    return new Intl.NumberFormat("en-NZ", {
      minimumFractionDigits: options.minimumFractionDigits || 0,
      maximumFractionDigits:
        options.maximumFractionDigits === undefined ? 1 : options.maximumFractionDigits
    }).format(number);
  }

  function parseDate(value) {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    const raw = String(value);
    const date = /^\d{4}-\d{2}-\d{2}$/.test(raw)
      ? new Date(`${raw}T00:00:00`)
      : new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  function formatDate(value, fallback = "Not recorded", style = "short") {
    const date = parseDate(value);
    if (!date) return value ? String(value) : fallback;
    return new Intl.DateTimeFormat("en-NZ", {
      day: "numeric",
      month: style === "long" ? "long" : "short",
      year: "numeric"
    }).format(date);
  }

  function formatTime(value) {
    const date = parseDate(value) || new Date();
    return new Intl.DateTimeFormat("en-NZ", {
      hour: "numeric",
      minute: "2-digit"
    }).format(date);
  }

  function sanitiseFilenamePart(value) {
    const cleaned = String(value || "")
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .replace(/-+/g, "-");
    return cleaned || "My-Block";
  }

  function buildFilename(reportData, options = {}) {
    if (options.filename) {
      const supplied = String(options.filename);
      return supplied.toLowerCase().endsWith(".pdf") ? supplied : `${supplied}.pdf`;
    }
    return `${sanitiseFilenamePart(reportData.farm.name)}-Farm-Management-Report.pdf`;
  }

  function dispatch(name, detail = {}) {
    document.dispatchEvent(
      new CustomEvent(name, {
        detail: {
          reportType: REPORT_TYPE,
          ...detail
        }
      })
    );
  }

  function requireStorage() {
    const storage = window.SMSFarmStorage;
    if (!storage || typeof storage.loadProfile !== "function") {
      throw new Error("SMSReportEngine requires SMSFarmStorage.");
    }
    return storage;
  }

  function hasPdfMakeCore() {
    return Boolean(
      window.pdfMake &&
      typeof window.pdfMake.createPdf === "function"
    );
  }

  function hasLegacyPdfMakeFonts() {
    return Boolean(
      hasPdfMakeCore() &&
      window.pdfMake.vfs &&
      typeof window.pdfMake.vfs === "object" &&
      Object.keys(window.pdfMake.vfs).length > 0
    );
  }

  function hasModernPdfMakeApi() {
    return Boolean(
      hasPdfMakeCore() &&
      typeof window.pdfMake.addVirtualFileSystem === "function"
    );
  }

  function hasConfiguredPdfFonts() {
    return hasLegacyPdfMakeFonts() || modernFontsRegistered;
  }

  function requirePdfMake() {
    if (!hasPdfMakeCore()) {
      throw new Error("The PDF generator is unavailable.");
    }

    if (!hasConfiguredPdfFonts()) {
      throw new Error("The PDF font library is unavailable.");
    }

    return window.pdfMake;
  }

  function deriveSiblingAssetUrl(assetName) {
    if (!ENGINE_SCRIPT_URL) {
      throw new Error(
        "SMSReportEngine could not determine its Shopify asset location."
      );
    }

    let engineUrl;

    try {
      engineUrl = new URL(ENGINE_SCRIPT_URL, window.location.href);
    } catch (error) {
      throw new Error(
        "SMSReportEngine could not read its Shopify asset URL.",
        { cause: error }
      );
    }

    const pathParts = engineUrl.pathname.split("/");
    pathParts[pathParts.length - 1] = assetName;
    engineUrl.pathname = pathParts.join("/");

    // Shopify asset paths work without copying the engine's version query.
    engineUrl.search = "";
    engineUrl.hash = "";

    return engineUrl.href;
  }

  function dependencyUrl(optionName, assetName) {
    const configured = String(
      engineOptions[optionName] || ""
    ).trim();

    if (configured) {
      return configured;
    }

    return deriveSiblingAssetUrl(assetName);
  }

  function findDependencyScript(key, url) {
    return (
      document.querySelector(
        `script[data-sms-report-engine="${key}"]`
      ) ||
      Array.from(document.scripts || []).find(script => {
        const source = String(script.src || "");
        return source === url || source.startsWith(`${url}?`);
      }) ||
      null
    );
  }

  function markDependencyScript(script, key) {
    if (script && !script.dataset.smsReportEngine) {
      script.dataset.smsReportEngine = key;
    }
  }

  function waitForCondition(validate, timeoutMs, errorMessage) {
    const startedAt = Date.now();

    return new Promise((resolve, reject) => {
      function check() {
        try {
          if (validate()) {
            resolve();
            return;
          }
        } catch (error) {
          reject(error);
          return;
        }

        if (Date.now() - startedAt >= timeoutMs) {
          reject(new Error(errorMessage));
          return;
        }

        window.setTimeout(check, 25);
      }

      check();
    });
  }

  function loadDependencyScript({
    key,
    url,
    validate = null,
    loadingMessage,
    failureMessage
  }) {
    if (typeof validate === "function" && validate()) {
      return Promise.resolve();
    }

    if (scriptPromises.has(key)) {
      return scriptPromises.get(key);
    }

    const promise = new Promise((resolve, reject) => {
      let script = findDependencyScript(key, url);
      let timeoutId = null;
      let settled = false;

      function cleanup() {
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }

        if (script) {
          script.removeEventListener("load", handleLoad);
          script.removeEventListener("error", handleError);
        }
      }

      function succeed() {
        if (settled) return;
        settled = true;
        cleanup();
        resolve();
      }

      function fail(error) {
        if (settled) return;
        settled = true;
        cleanup();

        if (
          script &&
          script.dataset.smsReportEngineState === "failed"
        ) {
          script.remove();
        }

        reject(error);
      }

      async function handleLoad() {
        if (script) {
          script.dataset.smsReportEngineState = "loaded";
        }

        try {
          if (typeof validate === "function") {
            await waitForCondition(
              validate,
              2000,
              failureMessage
            );
          }

          succeed();
        } catch (error) {
          fail(new Error(failureMessage, { cause: error }));
        }
      }

      function handleError(event) {
        if (script) {
          script.dataset.smsReportEngineState = "failed";
        }

        fail(new Error(failureMessage, { cause: event }));
      }

      log(loadingMessage);

      if (!script) {
        script = document.createElement("script");
        script.src = url;
        script.async = true;
        script.dataset.smsReportEngine = key;
        script.dataset.smsReportEngineState = "loading";
        script.addEventListener("load", handleLoad, { once: true });
        script.addEventListener("error", handleError, { once: true });

        const parent =
          document.head ||
          document.body ||
          document.documentElement;

        parent.appendChild(script);
      } else {
        markDependencyScript(script, key);

        if (
          script.dataset.smsReportEngineState === "failed"
        ) {
          script.remove();
          scriptPromises.delete(key);
          loadDependencyScript({
            key,
            url,
            validate,
            loadingMessage,
            failureMessage
          }).then(resolve, reject);
          return;
        }

        script.addEventListener("load", handleLoad, { once: true });
        script.addEventListener("error", handleError, { once: true });

        if (typeof validate === "function" && validate()) {
          succeed();
          return;
        }

        if (
          script.dataset.smsReportEngineState === "loaded" ||
          script.readyState === "complete" ||
          script.readyState === "loaded"
        ) {
          handleLoad();
        }
      }

      timeoutId = window.setTimeout(() => {
        if (script) {
          script.dataset.smsReportEngineState = "failed";
        }

        fail(new Error(failureMessage));
      }, DEPENDENCY_TIMEOUT_MS);
    }).catch(error => {
      scriptPromises.delete(key);
      throw error;
    });

    scriptPromises.set(key, promise);
    return promise;
  }

  function looksLikeVirtualFileSystem(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return false;
    }

    const entries = Object.entries(value);

    if (!entries.length) {
      return false;
    }

    return entries.some(([key, fileValue]) => {
      const filename = String(key || "").toLowerCase();
      const isFontFile =
        filename.endsWith(".ttf") ||
        filename.endsWith(".otf") ||
        filename.endsWith(".ttc") ||
        filename.endsWith(".woff") ||
        filename.endsWith(".woff2");

      return isFontFile && typeof fileValue === "string" && fileValue.length > 100;
    });
  }

  function extractVirtualFileSystem(value, seen = new Set(), depth = 0) {
    if (
      value === null ||
      value === undefined ||
      depth > 5 ||
      (typeof value !== "object" && typeof value !== "function")
    ) {
      return null;
    }

    if (seen.has(value)) {
      return null;
    }

    seen.add(value);

    if (looksLikeVirtualFileSystem(value)) {
      return value;
    }

    const preferredKeys = [
      "vfs",
      "pdfMake",
      "default",
      "fonts",
      "virtualFileSystem",
      "fileSystem"
    ];

    for (const key of preferredKeys) {
      let child;

      try {
        child = value[key];
      } catch (error) {
        continue;
      }

      const found = extractVirtualFileSystem(
        child,
        seen,
        depth + 1
      );

      if (found) {
        return found;
      }
    }

    let keys = [];

    try {
      keys = Object.keys(value).slice(0, 100);
    } catch (error) {
      return null;
    }

    for (const key of keys) {
      if (preferredKeys.includes(key)) {
        continue;
      }

      let child;

      try {
        child = value[key];
      } catch (error) {
        continue;
      }

      const found = extractVirtualFileSystem(
        child,
        seen,
        depth + 1
      );

      if (found) {
        return found;
      }
    }

    return null;
  }

  function captureWindowKeys() {
    try {
      return new Set(Object.getOwnPropertyNames(window));
    } catch (error) {
      return new Set();
    }
  }

  function findFontPayload(previousWindowKeys = new Set()) {
    const directCandidates = [
      window.pdfMakeFonts,
      window.pdfFonts,
      window.vfsFonts,
      window.pdfmakeFonts,
      window.pdfMakeVfs,
      window.pdfMake?.vfs
    ];

    for (const candidate of directCandidates) {
      const found = extractVirtualFileSystem(candidate);
      if (found) {
        return found;
      }
    }

    let windowKeys = [];

    try {
      windowKeys = Object.getOwnPropertyNames(window);
    } catch (error) {
      return null;
    }

    const newKeys = windowKeys.filter(
      key => !previousWindowKeys.has(key)
    );

    for (const key of newKeys) {
      let candidate;

      try {
        candidate = window[key];
      } catch (error) {
        continue;
      }

      const found = extractVirtualFileSystem(candidate);
      if (found) {
        return found;
      }
    }

    return null;
  }

  function registerModernVirtualFileSystem(payload) {
    if (!payload || !hasModernPdfMakeApi()) {
      return false;
    }

    window.pdfMake.addVirtualFileSystem(payload);
    modernFontsRegistered = true;
    log("Registered PDF fonts through addVirtualFileSystem().");
    return true;
  }

  function createFontValidationDocument() {
    return {
      content: [
        {
          text: "Sheep Made Simple",
          fontSize: 8
        }
      ],
      defaultStyle: {
        font: "Roboto"
      }
    };
  }

  function validatePdfGeneration() {
    if (hasLegacyPdfMakeFonts()) {
      return Promise.resolve(true);
    }

    if (!hasPdfMakeCore()) {
      return Promise.reject(
        new Error("The PDF generator is unavailable.")
      );
    }

    if (fontValidationPromise) {
      return fontValidationPromise;
    }

    fontValidationPromise = new Promise((resolve, reject) => {
      let settled = false;
      let timeoutId = null;

      function finish(error) {
        if (settled) return;
        settled = true;

        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }

        if (error) {
          reject(error);
        } else {
          modernFontsRegistered = true;
          resolve(true);
        }
      }

      try {
        const pdf = window.pdfMake.createPdf(
          createFontValidationDocument()
        );

        if (!pdf || typeof pdf.getBuffer !== "function") {
          throw new Error(
            "The PDF generator did not return a usable document."
          );
        }

        const callback = buffer => {
          if (buffer) {
            finish();
          } else {
            finish(
              new Error(
                "The PDF font library could not be validated."
              )
            );
          }
        };

        const result = pdf.getBuffer(callback);

        if (result && typeof result.then === "function") {
          result.then(
            buffer => callback(buffer),
            error => finish(error)
          );
        }

        timeoutId = window.setTimeout(() => {
          finish(
            new Error(
              "The PDF font library could not be validated."
            )
          );
        }, FONT_VALIDATION_TIMEOUT_MS);
      } catch (error) {
        finish(error);
      }
    }).catch(error => {
      fontValidationPromise = null;
      modernFontsRegistered = false;
      throw error;
    });

    return fontValidationPromise;
  }

  async function ensurePdfDependencies() {
    if (pdfDependenciesReady && hasConfiguredPdfFonts()) {
      log("pdfMake already available.");
      return window.pdfMake;
    }

    if (dependencyPromise) {
      return dependencyPromise;
    }

    dependencyPromise = (async () => {
      if (!hasPdfMakeCore()) {
        const pdfMakeUrl = dependencyUrl(
          "pdfMakeUrl",
          PDFMAKE_ASSET_NAME
        );

        await loadDependencyScript({
          key: "pdfmake",
          url: pdfMakeUrl,
          validate: hasPdfMakeCore,
          loadingMessage: "Loading local pdfMake asset...",
          failureMessage: "Could not load pdfMake."
        });
      } else {
        log("pdfMake core already available.");
      }

      if (!hasPdfMakeCore()) {
        throw new Error("The PDF generator is unavailable.");
      }

      if (hasLegacyPdfMakeFonts()) {
        pdfDependenciesReady = true;
        log("Legacy PDF font library already available.");
        return window.pdfMake;
      }

      const beforeFontWindowKeys = captureWindowKeys();
      const pdfFontsUrl = dependencyUrl(
        "pdfFontsUrl",
        PDF_FONTS_ASSET_NAME
      );

      await loadDependencyScript({
        key: "pdfmake-fonts",
        url: pdfFontsUrl,
        loadingMessage: "Loading local PDF font asset...",
        failureMessage: "Could not load the PDF font library."
      });

      if (!hasLegacyPdfMakeFonts()) {
        const payload = findFontPayload(
          beforeFontWindowKeys
        );

        if (payload) {
          registerModernVirtualFileSystem(payload);
        }
      }

      try {
        await validatePdfGeneration();
      } catch (error) {
        throw new Error(
          "The PDF font library is unavailable.",
          { cause: error }
        );
      }

      const pdfMake = requirePdfMake();
      pdfDependenciesReady = true;
      log("PDF dependencies ready.");
      return pdfMake;
    })().catch(error => {
      pdfDependenciesReady = false;
      dependencyPromise = null;
      throw error;
    });

    return dependencyPromise;
  }

  function normaliseProfile(rawProfile) {
    const raw = isObject(rawProfile) ? rawProfile : {};
    const myBlock = isObject(raw.myBlock) ? raw.myBlock : {};

    return {
      raw,
      property: isObject(myBlock.property) ? clone(myBlock.property) : {},
      paddocks: Array.isArray(myBlock.paddocks) ? clone(myBlock.paddocks) : [],
      flock: isObject(myBlock.flock) ? clone(myBlock.flock) : {},
      capacity: isObject(myBlock.capacity) ? clone(myBlock.capacity) : {},
      breeding: isObject(myBlock.breeding) ? clone(myBlock.breeding) : {},
      grazing: isObject(myBlock.grazing) ? clone(myBlock.grazing) : {},
      importantPaddocks: isObject(myBlock.importantPaddocks)
        ? clone(myBlock.importantPaddocks)
        : {},
      records: isObject(myBlock.records)
        ? clone(myBlock.records)
        : isObject(raw.records)
          ? clone(raw.records)
          : {},
      notes: isObject(myBlock.notes)
        ? clone(myBlock.notes)
        : isObject(raw.notes)
          ? clone(raw.notes)
          : {},
      decisionStatus: isObject(raw.decisionStatus) ? clone(raw.decisionStatus) : {},
      decisionHistory: Array.isArray(raw.decisionHistory)
        ? clone(raw.decisionHistory)
        : []
    };
  }

  async function fileToDataUrl(file) {
    if (!file) return "";
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(reader.error || new Error("Map file could not be read."));
      reader.readAsDataURL(file);
    });
  }

  async function loadMap(type, storage) {
    try {
      if (typeof storage.loadMap !== "function") return "";
      const record = await storage.loadMap(type);
      if (!record || !record.file) return "";
      return await fileToDataUrl(record.file);
    } catch (error) {
      warn(`Could not load ${type} map.`, error);
      return "";
    }
  }

  async function loadMaps(storage) {
    const entries = await Promise.all(
      MAP_TYPES.map(async type => [type, await loadMap(type, storage)])
    );
    return Object.fromEntries(entries);
  }

  function activePaddocks(profile) {
    return profile.paddocks.filter(item => item && item.active !== false && item.active !== "no");
  }

  function paddockLookup(paddocks) {
    const byId = new Map(paddocks.map(item => [item.id, item]));
    return id => {
      if (id === "not-applicable" || id === "not_applicable") {
        return "Not applicable this season";
      }
      return byId.get(id)?.name || "Not selected";
    };
  }

  function calculateTotalHead(flock) {
    const stored = Number(flock.totalHeadCount);
    if (Number.isFinite(stored) && stored > 0) return stored;
    return [
      flock.adultEwes,
      flock.adultWethers,
      flock.adultRams,
      flock.xlAdults,
      flock.hoggets,
      flock.lambs,
      flock.smallBreedSheep
    ].reduce((total, value) => total + numberOr(value), 0);
  }

  function calculateSheepEquivalents(flock) {
    const stored = Number(flock.sheepEquivalents);
    if (Number.isFinite(stored) && stored > 0) return stored;
    return (
      numberOr(flock.adultEwes) +
      numberOr(flock.adultWethers) +
      numberOr(flock.adultRams) +
      numberOr(flock.xlAdults) * 1.5 +
      numberOr(flock.hoggets) * 0.75 +
      numberOr(flock.lambs) * 0.5 +
      numberOr(flock.smallBreedSheep) * 0.75
    );
  }

  function buildFarmMeta(profile, generatedAt) {
    const farmName = safeText(profile.property.farmName, "My Block");
    const location = [profile.property.nearestTown, profile.property.region]
      .filter(Boolean)
      .join(", ");

    return {
      name: farmName,
      location: location || "Location not recorded",
      generatedAt: generatedAt.toISOString(),
      generatedDate: formatDate(generatedAt, "", "long"),
      generatedTime: formatTime(generatedAt),
      areaUnit: safeText(profile.property.areaUnit, "acres")
    };
  }

  function buildPropertySummary(profile, farm) {
    return {
      farmName: farm.name,
      region: safeText(profile.property.region),
      nearestTown: safeText(profile.property.nearestTown),
      address: safeText(profile.property.address),
      totalPropertyArea: profile.property.totalPropertyArea,
      usableGrazingArea: profile.property.usableGrazingArea,
      areaUnit: farm.areaUnit,
      totalPropertyDisplay:
        profile.property.totalPropertyArea === undefined ||
        profile.property.totalPropertyArea === null ||
        profile.property.totalPropertyArea === ""
          ? "Not recorded"
          : `${formatNumber(profile.property.totalPropertyArea)} ${farm.areaUnit}`,
      usableGrazingDisplay:
        profile.property.usableGrazingArea === undefined ||
        profile.property.usableGrazingArea === null ||
        profile.property.usableGrazingArea === ""
          ? "Not recorded"
          : `${formatNumber(profile.property.usableGrazingArea)} ${farm.areaUnit}`
    };
  }

  function buildPaddockSummary(profile, farm) {
    const durationLabels = {
      half: "< 1 week",
      one: "1 week",
      two: "2 weeks",
      three: "3 weeks",
      variable: "Variable",
      unsure: "Not set"
    };

    return activePaddocks(profile).map((paddock, index) => ({
      id: paddock.id,
      order: numberOr(paddock.order, index + 1),
      name: safeText(paddock.name, `Paddock ${index + 1}`),
      area: paddock.area,
      areaDisplay:
        paddock.area === undefined || paddock.area === null || paddock.area === ""
          ? "Not recorded"
          : `${formatNumber(paddock.area, { maximumFractionDigits: 2 })} ${farm.areaUnit}`,
      normalDuration: paddock.normalDuration,
      normalDurationDisplay: durationLabels[paddock.normalDuration] || "Not set",
      canSplit: humanise(paddock.canSplit, "No"),
      drainage: humanise(paddock.drainage, "Not set"),
      shelter: humanise(paddock.shelter, "Not set"),
      waterAvailable: humanise(paddock.waterAvailable, "Not set"),
      canHandleExtraPressure: humanise(paddock.canHandleExtraPressure, "Not set")
    }));
  }

  function buildFlockSummary(profile) {
    const totalHead = calculateTotalHead(profile.flock);
    const sheepEquivalents = calculateSheepEquivalents(profile.flock);
    const expectedLambs = numberOr(profile.breeding.expectedLambs, null);
    const calculatedExpectedLambs =
      profile.breeding.breedsSheep === "yes" &&
      numberOr(profile.breeding.breedingEwes) > 0 &&
      numberOr(profile.breeding.expectedLambingPercentage) > 0
        ? Math.round(
            numberOr(profile.breeding.breedingEwes) *
              (numberOr(profile.breeding.expectedLambingPercentage) / 100)
          )
        : null;

    return {
      totalHead,
      sheepEquivalents: Math.round(sheepEquivalents * 100) / 100,
      categories: {
        adultEwes: numberOr(profile.flock.adultEwes),
        adultWethers: numberOr(profile.flock.adultWethers),
        adultRams: numberOr(profile.flock.adultRams),
        xlAdults: numberOr(profile.flock.xlAdults),
        hoggets: numberOr(profile.flock.hoggets),
        lambs: numberOr(profile.flock.lambs),
        smallBreedSheep: numberOr(profile.flock.smallBreedSheep)
      },
      purpose: humanise(profile.flock.flockPurpose),
      capacity: clone(profile.capacity),
      breeding: {
        ...clone(profile.breeding),
        expectedLambs:
          expectedLambs !== null ? expectedLambs : calculatedExpectedLambs
      }
    };
  }

  function buildGrazingFallback(profile, paddocks) {
    const durationDays = { half: 3.5, one: 7, two: 14, three: 21 };
    const rotationDays = paddocks.reduce(
      (total, paddock) => total + numberOr(durationDays[paddock.normalDuration], 7),
      0
    );

    return {
      source: "profile",
      currentRotation: {
        days: rotationDays,
        weeks: Math.round((rotationDays / 7) * 10) / 10,
        activePaddockCount: paddocks.length,
        grazingShiftCount: paddocks.length
      },
      maximumRotation: {
        days: numberOr(profile.grazing.maximumRotationDays, rotationDays),
        weeks: numberOr(
          profile.grazing.maximumRotationWeeks,
          Math.round((rotationDays / 7) * 10) / 10
        ),
        maximumShiftCount: numberOr(profile.grazing.maximumTemporaryShifts, paddocks.length)
      },
      currentPaddockId: profile.grazing.currentPaddockId || "",
      target: null,
      recommendation: null,
      raw: clone(profile.grazing)
    };
  }

  function buildGrazingSummary(profile, paddocks) {
    try {
      if (
        window.SMSGrazingEngine &&
        typeof window.SMSGrazingEngine.getSummary === "function"
      ) {
        return {
          source: "engine",
          ...clone(window.SMSGrazingEngine.getSummary(profile.raw))
        };
      }
    } catch (error) {
      warn("SMSGrazingEngine summary was unavailable; using stored profile values.", error);
    }
    return buildGrazingFallback(profile, paddocks);
  }

  const SEASON_LABELS = Object.freeze({
    winter: "Winter",
    spring: "Spring",
    summer: "Summer",
    autumn: "Autumn"
  });

  const SEASON_SUBTITLES = Object.freeze({
    winter: "Make grass last, keep sheep comfortable and prepare for lambing.",
    spring: "Manage faster growth, lambing and the shift into higher pasture supply.",
    summer: "Protect pasture quality, prepare for dry periods and manage breeding dates.",
    autumn: "Set up the block for slower growth and the coming winter."
  });

  function getNewZealandYear(date = new Date()) {
    try {
      const parts = new Intl.DateTimeFormat("en-NZ", {
        timeZone: "Pacific/Auckland",
        year: "numeric"
      }).formatToParts(date);

      const yearPart = parts.find(part => part.type === "year");
      const year = Number(yearPart?.value);

      return Number.isFinite(year) ? year : date.getFullYear();
    } catch (error) {
      return date.getFullYear();
    }
  }

  function valueAtPath(source, path, fallback = undefined) {
    if (!source || !path) return fallback;

    const value = String(path)
      .split(".")
      .filter(Boolean)
      .reduce(
        (current, key) =>
          current === undefined || current === null
            ? undefined
            : current[key],
        source
      );

    return value === undefined ? fallback : value;
  }

  function firstDefined(values, fallback = undefined) {
    for (const value of values) {
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }

    return fallback;
  }

  function sharedSummaryStatus(summary, percentage) {
    const rawStatus = String(
      firstDefined([
        summary?.status,
        summary?.planStatus,
        summary?.state
      ], "")
    )
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");

    if (percentage >= 100) return "Complete";
    if (percentage > 0) return "In progress";

    if (
      ["complete", "completed"].includes(rawStatus)
    ) {
      return "Complete";
    }

    if (
      ["in_progress", "started", "active"].includes(rawStatus)
    ) {
      return "In progress";
    }

    return "Not started";
  }
  function normaliseSharedSummary(summary) {
    summary = isObject(summary) ? summary : {};
    const percentageValue = Number(
      firstDefined([
        summary.percentage,
        summary.progressPercent,
        summary.progress
      ], 0)
    );

    const percentage = clamp(
      Number.isFinite(percentageValue) ? percentageValue : 0,
      0,
      100
    );

    const updatedAt = summary.updatedAt || null;
    const completedAt = summary.completedAt || null;

    return {
      status: sharedSummaryStatus(summary, percentage),
      percentage,
      completedTasks: numberOr(summary.completedTasks, 0),
      totalTasks: numberOr(summary.totalTasks, 0),
      updatedAt,
      completedAt,
      updatedLabel: summary.updatedLabel || "Not updated",
      tasks: clone(summary.tasks || [])
    };
  }

  async function resolveSeasonSummary({ season, year, plan }) {
    const seasonalSummary = window.SMSSeasonalSummary;

    if (!seasonalSummary || typeof seasonalSummary.getPlanSummary !== "function") {
      warn(`Seasonal Summary unavailable for ${season}.`);
      return normaliseSharedSummary({});
    }

    try {
      const result = await seasonalSummary.getPlanSummary({ season, year, plan });
      return normaliseSharedSummary(isObject(result?.data) ? result.data : result);
    } catch (error) {
      warn(`Seasonal Summary unavailable for ${season}.`, error);
      return normaliseSharedSummary({});
    }
  }

  async function resolveSeasonPlan({ season, year }) {
    const engine = window.SMSSeasonalEngine;

    if (!engine || typeof engine.getPlan !== "function") {
      warn(`Seasonal Plan API unavailable for ${season}.`);
      return null;
    }

    try {
      const result = await engine.getPlan({ season, year, force: true });
      if (result?.success === true && isObject(result.data)) return clone(result.data);
      warn(`Seasonal Plan unavailable for ${season}.`, result);
      return null;
    } catch (error) {
      warn(`Seasonal Plan unavailable for ${season}.`, error);
      return null;
    }
  }

  function taskRecord(plan, taskId) {
    const task = plan?.tasks?.[taskId];
    return isObject(task) ? task : null;
  }

  function taskCompleted(plan, taskId) {
    return taskRecord(plan, taskId)?.meta?.completed === true;
  }

  function taskData(plan, taskId) {
    const data = taskRecord(plan, taskId)?.data;
    return isObject(data) ? data : {};
  }

  function firstImportantPaddock(profile, keys) {
    const store = isObject(profile?.importantPaddocks) ? profile.importantPaddocks : {};
    return firstDefined(keys.map(key => valueAtPath(store, key)), null);
  }

  function resolvePaddock({ planValue, completed, fallbackValue, paddockName, notApplicable = false }) {
    if (completed && notApplicable) return "Not applicable this season";
    if (completed && planValue) return paddockName(planValue);
    if (fallbackValue) return paddockName(fallbackValue);
    return "Not selected";
  }

  function formatList(values, fallback = "Not selected") {
    if (!Array.isArray(values) || !values.length) return fallback;
    return values.map(value => humanise(value, "")).filter(Boolean).join(", ");
  }

  function numberText(value, suffix = "") {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? `${number}${suffix}` : "Not planned";
  }

  function genericTaskValue(data) {
    if (!isObject(data)) return "Not reviewed";
    const preferred = [
      "status", "currentStatus", "lambingStatus", "shearingStatus",
      "paddockId", "plannedRotationWeeks", "currentRotation",
      "grassStatus", "wormRisk", "feetStatus", "lambGrowth", "applicability"
    ];

    for (const key of preferred) {
      const value = data[key];
      if (value === undefined || value === null || value === "") continue;
      if (key === "plannedRotationWeeks") return numberText(value, " weeks");
      if (Array.isArray(value)) return formatList(value);
      if (typeof value === "boolean") return value ? "Yes" : "No";
      return humanise(value, "Reviewed");
    }

    return Object.keys(data).length ? "Reviewed" : "Not reviewed";
  }

  function genericSeasonRows({ season, plan }) {
    const tasks = isObject(plan?.tasks) ? plan.tasks : {};
    const definition = window.SMSSeasonalEngine?.getDefinition?.(season);
    const taskIds = Array.isArray(definition?.taskOrder) && definition.taskOrder.length
      ? definition.taskOrder
      : Object.keys(tasks);

    if (!taskIds.length) return [[`${SEASON_LABELS[season]} decisions`, "Not started"]];

    return taskIds.map(taskId => [
      humanise(taskId, "Seasonal decision"),
      taskCompleted(plan, taskId) ? genericTaskValue(taskData(plan, taskId)) : "Not reviewed"
    ]);
  }

  const SeasonalReportAdapters = Object.freeze({
    winter({ plan, profile, paddockName }) {
      const winter = taskData(plan, "winterPaddock");
      const lambing = taskData(plan, "lambingPaddock");
      const grazing = taskData(plan, "grazingRotation");
      const preparation = taskData(plan, "lambingPreparation");
      const early = taskData(plan, "earlyLambing");

      const winterFallback = firstImportantPaddock(profile, [
        "winterPaddockId", "winter.paddockId", "winterPaddock", "winter"
      ]);
      const lambingFallback = firstImportantPaddock(profile, [
        "lambingPaddockId", "lambing.paddockId", "lambingPaddock", "lambing"
      ]);

      const preparationValue = preparation.applicability === "not_applicable"
        ? "Not applicable this season"
        : [
            preparation.highRiskEwesReviewed ? "High-risk ewes reviewed" : null,
            preparation.suppliesPrepared ? "Supplies prepared" : null,
            preparation.helpPlanReady ? "Help plan ready" : null
          ].filter(Boolean).join(", ") || "Not reviewed";

      const earlyValue = early.applicability === "not_applicable"
        ? "Not applicable this season"
        : [
            early.dailyChecks ? humanise(early.dailyChecks) : null,
            early.newbornFeedingKnowledge === "reviewed" ? "Newborn feeding reviewed" : null,
            early.severeWeatherPlan === "prepared" ? "Severe-weather plan prepared" : null
          ].filter(Boolean).join(", ") || "Not reviewed";

      return [
        ["Winter paddock", resolvePaddock({
          planValue: winter.paddockId,
          completed: taskCompleted(plan, "winterPaddock"),
          fallbackValue: winterFallback,
          paddockName
        })],
        ["Lambing paddock", resolvePaddock({
          planValue: lambing.paddockId,
          completed: taskCompleted(plan, "lambingPaddock"),
          fallbackValue: lambingFallback,
          paddockName,
          notApplicable: lambing.applicability === "not_applicable"
        })],
        ["Planned rotation", taskCompleted(plan, "grazingRotation")
          ? numberText(grazing.plannedRotationWeeks, " weeks") : "Not planned"],
        ["Grazing shifts", taskCompleted(plan, "grazingRotation")
          ? numberText(grazing.plannedGrazingShifts) : "Not planned"],
        ["Lambing preparation", taskCompleted(plan, "lambingPreparation")
          ? preparationValue : "Not reviewed"],
        ["Early lambing", taskCompleted(plan, "earlyLambing")
          ? earlyValue : "Not reviewed"]
      ];
    },

    spring({ plan }) {
      const lambing = taskData(plan, "lambingManagement");
      const support = taskData(plan, "eweAndLambSupport");
      const grazing = taskData(plan, "grazingRotation");
      const health = taskData(plan, "springHealth");
      const procedures = taskData(plan, "lambProcedures");
      const shearing = taskData(plan, "shearingAndFlystrike");

      const procedureValue = procedures.status === "not_required"
        ? "Not required"
        : procedures.status === "planned"
          ? [
              "Planned",
              procedures.plannedDate ? formatDate(procedures.plannedDate, "") : null,
              Number(procedures.plannedLambCount) > 0 ? `${procedures.plannedLambCount} lambs` : null,
              formatList(procedures.procedures, "")
            ].filter(Boolean).join(" · ")
          : procedures.status === "completed"
            ? [
                "Completed",
                procedures.completedDate ? formatDate(procedures.completedDate, "") : null,
                Number(procedures.completedLambCount) > 0 ? `${procedures.completedLambCount} lambs` : null,
                formatList(procedures.procedures, "")
              ].filter(Boolean).join(" · ")
            : "Not reviewed";

      const shearingValue = [
        shearing.shearingStatus ? humanise(shearing.shearingStatus) : null,
        shearing.shearingDate ? formatDate(shearing.shearingDate, "") : null,
        shearing.flystrikeMonitoring ? `Flystrike: ${humanise(shearing.flystrikeMonitoring)}` : null
      ].filter(Boolean).join(" · ") || "Not reviewed";

      return [
        ["Lambing management", taskCompleted(plan, "lambingManagement")
          ? [humanise(lambing.lambingStatus, "Not reviewed"), lambing.dailyChecks ? humanise(lambing.dailyChecks) : null].filter(Boolean).join(" · ")
          : "Not reviewed"],
        ["Ewe and lamb support", taskCompleted(plan, "eweAndLambSupport")
          ? `Ewes: ${humanise(support.eweCondition, "Not reviewed")} · Supplementation: ${humanise(support.supplementation, "Not reviewed")}`
          : "Not reviewed"],
        ["Grazing rotation", taskCompleted(plan, "grazingRotation")
          ? [humanise(grazing.currentRotation, "Not reviewed"), humanise(grazing.grassStatus, "Not reviewed"), formatList(grazing.growthStrategies, "")].filter(Boolean).join(" · ")
          : "Not reviewed"],
        ["Spring health", taskCompleted(plan, "springHealth")
          ? `Worm risk: ${humanise(health.wormRisk, "Not reviewed")} · Feet: ${humanise(health.feetStatus, "Not reviewed")} · Lamb growth: ${humanise(health.lambGrowth, "Not reviewed")}`
          : "Not reviewed"],
        ["Lamb procedures", taskCompleted(plan, "lambProcedures") ? procedureValue : "Not reviewed"],
        ["Shearing and flystrike", taskCompleted(plan, "shearingAndFlystrike") ? shearingValue : "Not reviewed"]
      ];
    },

    summer(context) {
      return genericSeasonRows(context);
    },

    autumn(context) {
      return genericSeasonRows(context);
    }
  });

  function adaptSeasonPlan({ season, plan, profile, paddockName }) {
    const adapter = SeasonalReportAdapters[season];
    try {
      return typeof adapter === "function"
        ? adapter({ season, plan, profile, paddockName })
        : genericSeasonRows({ season, plan });
    } catch (error) {
      warn(`Seasonal Report Adapter failed for ${season}; using generic task output.`, error);
      return genericSeasonRows({ season, plan });
    }
  }

  async function buildSeasonModel({
    season,
    year,
    profile,
    maps,
    paddockName
  }) {
    const plan = await resolveSeasonPlan({ season, year });
    const summary = await resolveSeasonSummary({ season, year, plan });

    return {
      key: season,
      label: SEASON_LABELS[season],
      year,
      subtitle: SEASON_SUBTITLES[season],
      status: summary.status,
      progressPercent: summary.percentage,
      completedTasks: summary.completedTasks,
      totalTasks: summary.totalTasks,
      updatedAt: summary.updatedAt,
      completedAt: summary.completedAt,
      updatedLabel: summary.updatedLabel,
      tasks: summary.tasks,
      rows: adaptSeasonPlan({ season, plan, profile, paddockName }).map(([label, value]) => ({ label, value })),
      map: maps[season] || "",
      rawPlan: plan ? clone(plan) : null,
      raw: plan ? clone(plan) : null,
      source: {
        summary: "SMSSeasonalSummary",
        plan: "SMSSeasonalEngine.getPlan",
        adapter: `SeasonalReportAdapters.${season}`
      }
    };
  }

  async function buildSeasonalSummary(
    profile,
    maps,
    paddockName,
    options = {}
  ) {
    const generatedAt = options.generatedAt
      ? parseDate(options.generatedAt) || new Date()
      : new Date();

    const currentYear = getNewZealandYear(generatedAt);
    const explicitYears = isObject(options.seasonYears)
      ? options.seasonYears
      : {};

    const entries = await Promise.all(
      SEASONS.map(async season => {
        const explicitYear = firstDefined([
          explicitYears[season],
          options.seasonYear,
          options.year
        ], null);

        const resolvedYear = Number.isFinite(Number(explicitYear))
          ? Number(explicitYear)
          : currentYear;

        const data = await buildSeasonModel({
          season,
          year: resolvedYear,
          profile,
          maps,
          paddockName
        });

        return [season, data];
      })
    );

    return Object.fromEntries(entries);
  }

  function guideName(record) {
    if (record?.guideName) return record.guideName;
    const names = {
      do_i_need_to_drench: "Do I Need to Drench?",
      "do-i-need-to-drench": "Do I Need to Drench?",
      drench: "Do I Need to Drench?",
      should_i_call_a_vet: "Should I Call a Vet?",
      "should-i-call-a-vet": "Should I Call a Vet?"
    };
    return names[record?.guideKey] || humanise(record?.guideKey, "Decision Guide");
  }

  function outcomeName(code, record) {
    if (record?.outcomeData?.outcomeLabel) return record.outcomeData.outcomeLabel;
    if (record?.outcomeLabel) return record.outcomeLabel;
    const labels = {
      no_drench_needed: "No drench needed",
      drench_not_needed: "No drench needed",
      monitor: "Monitor and review",
      monitor_and_review: "Monitor and review",
      worm_count: "Complete a worm count",
      worm_count_recommended: "Complete a worm count",
      drench_now: "Drench now",
      drenched: "Drenched",
      vet_required: "Call a vet",
      managed_at_home: "Managed at home"
    };
    return labels[code] || humanise(code, "Outcome not recorded");
  }

  function buildDecisionSummary(profile) {
    const history = profile.decisionHistory
      .slice()
      .sort(
        (a, b) =>
          (parseDate(b.reviewedAt || b.createdAt)?.getTime() || 0) -
          (parseDate(a.reviewedAt || a.createdAt)?.getTime() || 0)
      )
      .slice(0, MAX_DECISION_HISTORY)
      .map(record => ({
        ...clone(record),
        guideName: guideName(record),
        outcomeName: outcomeName(record.outcomeCode, record),
        reviewedDate: formatDate(record.reviewedAt || record.createdAt),
        nextReviewDate: formatDate(record.nextReviewAt, "Not set")
      }));

    const drench = isObject(profile.decisionStatus.drench)
      ? clone(profile.decisionStatus.drench)
      : {};
    const latest = history.find(item => item.decisionId === drench.latestDecisionId) || null;

    return {
      drench: {
        ...drench,
        hasStatus: Boolean(
          drench.latestOutcomeCode ||
            drench.lastReviewedAt ||
            drench.nextReviewAt ||
            drench.latestDecisionId
        ),
        outcomeName: drench.latestOutcomeCode
          ? outcomeName(drench.latestOutcomeCode, latest)
          : "No decision saved yet",
        lastReviewedDisplay: formatDate(drench.lastReviewedAt),
        nextReviewDisplay: formatDate(drench.nextReviewAt, "When conditions change"),
        lastDrenchedDisplay: formatDate(
          drench.lastDrenchedAt || profile.records.drench?.lastDone
        ),
        lastWormCountDisplay: formatDate(drench.lastWormCountAt)
      },
      history,
      historyLimit: MAX_DECISION_HISTORY
    };
  }

  function buildRecordsSummary(profile) {
    const records = profile.records;
    const rows = [
      ["Shearing", records.shearing?.lastDone, records.shearing?.nextDue],
      ["Drench", records.drench?.lastDone, records.drench?.nextDue],
      ["Fly treatment", records.flyTreatment?.lastDone, records.flyTreatment?.nextDue],
      ["Hoof trim", records.hoofTrim?.lastDone, records.hoofTrim?.nextDue],
      ["Ram in", profile.breeding.ramInDate, null],
      [
        "Ram out",
        profile.breeding.ramManagementType === "year_round"
          ? "With ewes year-round"
          : profile.breeding.ramOutDate,
        null
      ],
      [
        "Expected lambing",
        profile.breeding.expectedLambingStart,
        profile.breeding.expectedLambingEnd
      ]
    ];

    return {
      rows: rows.map(([label, lastDone, nextDue]) => ({
        label,
        lastDone,
        nextDue,
        lastDoneDisplay:
          label === "Ram out" && lastDone === "With ewes year-round"
            ? lastDone
            : formatDate(lastDone),
        nextDueDisplay: nextDue ? formatDate(nextDue, "Not set") : "—"
      })),
      raw: clone(records)
    };
  }

  function hasSeasonalProgress(seasonalPlans) {
    return Object.values(seasonalPlans).some(plan => plan.status !== "Not started");
  }

  function buildProfileReview(profile, paddocks, flock, seasonalPlans, decisions, records) {
    const checks = [
      {
        key: "property",
        label: "Property",
        complete: Boolean(profile.property.region && numberOr(profile.property.usableGrazingArea) > 0)
      },
      {
        key: "paddocks",
        label: "Paddocks",
        complete: paddocks.length >= 2
      },
      {
        key: "flock",
        label: "Flock",
        complete: flock.totalHead > 0
      },
      {
        key: "seasonalPlans",
        label: "Seasonal Plans",
        complete: hasSeasonalProgress(seasonalPlans)
      },
      {
        key: "decisions",
        label: "Decision Guides",
        complete: decisions.history.length > 0 || decisions.drench.hasStatus
      },
      {
        key: "records",
        label: "Records",
        complete: records.rows.some(row => row.lastDone && row.lastDone !== "With ewes year-round")
      }
    ];

    const completeCount = checks.filter(check => check.complete).length;
    const percentage = Math.round((completeCount / checks.length) * 100);
    const status = percentage >= 85
      ? "Complete"
      : percentage >= 55
        ? "Needs a few updates"
        : "Getting started";

    return {
      percentage,
      status,
      sections: checks,
      message:
        percentage >= 80
          ? "Your My Block profile is ready to support personalised recommendations."
          : "Complete the remaining sections to improve the confidence of future personalised recommendations."
    };
  }

  async function getFarmReportData(options = {}) {
    const storage = requireStorage();
    const generatedAt = options.generatedAt ? parseDate(options.generatedAt) || new Date() : new Date();
    const rawProfile = options.profile || (await storage.loadProfile());
    const profile = normaliseProfile(rawProfile);
    const maps = options.maps || (await loadMaps(storage));
    const farm = buildFarmMeta(profile, generatedAt);
    const property = buildPropertySummary(profile, farm);
    const paddocks = buildPaddockSummary(profile, farm);
    const paddockName = paddockLookup(activePaddocks(profile));
    const flock = buildFlockSummary(profile);
    const grazing = buildGrazingSummary(profile, activePaddocks(profile));
    const seasonalPlans = await buildSeasonalSummary(profile, maps, paddockName, options);
    const decisions = buildDecisionSummary(profile);
    const records = buildRecordsSummary(profile);
    const review = buildProfileReview(
      profile,
      paddocks,
      flock,
      seasonalPlans,
      decisions,
      records
    );

    return {
      meta: {
        engineVersion: VERSION,
        reportType: REPORT_TYPE,
        generatedAt: generatedAt.toISOString(),
        generatedDate: farm.generatedDate,
        generatedTime: farm.generatedTime
      },
      farm,
      property,
      paddocks,
      flock,
      grazing,
      seasonalPlans,
      decisions,
      records,
      notes: {
        general: safeText(profile.notes.general, "No notes added.")
      },
      maps,
      review,
      rawProfile: options.includeRawProfile ? clone(rawProfile) : undefined
    };
  }

  function tableLayout(theme) {
    return {
      fillColor(rowIndex) {
        return rowIndex === 0 ? theme.colours.green : rowIndex % 2 === 0 ? "#F8FAF8" : null;
      },
      hLineColor() {
        return theme.colours.line;
      },
      vLineColor() {
        return theme.colours.line;
      },
      paddingTop() {
        return 7;
      },
      paddingBottom() {
        return 7;
      },
      paddingLeft() {
        return 7;
      },
      paddingRight() {
        return 7;
      }
    };
  }

  function sectionTitle(title, kicker, theme) {
    return {
      stack: [
        kicker
          ? {
              text: kicker.toUpperCase(),
              fontSize: 8,
              bold: true,
              color: theme.colours.green,
              characterSpacing: 1.2,
              margin: [0, 0, 0, 5]
            }
          : null,
        {
          text: title,
          fontSize: 24,
          bold: true,
          color: theme.colours.dark,
          margin: [0, 0, 0, 14]
        }
      ].filter(Boolean),
      unbreakable: true
    };
  }

  function statCard(label, value, note, theme) {
    return {
      width: "*",
      table: {
        widths: ["*"],
        body: [
          [
            {
              stack: [
                {
                  text: String(label).toUpperCase(),
                  fontSize: 7.5,
                  bold: true,
                  color: theme.colours.muted,
                  characterSpacing: 0.7
                },
                {
                  text: String(value),
                  fontSize: 17,
                  bold: true,
                  color: theme.colours.dark,
                  margin: [0, 5, 0, note ? 3 : 0]
                },
                note ? { text: String(note), fontSize: 8, color: theme.colours.muted } : null
              ].filter(Boolean),
              margin: [11, 10, 11, 10],
              fillColor: theme.colours.pale
            }
          ]
        ]
      },
      layout: "noBorders"
    };
  }

  function infoRow(label, value, theme) {
    return [
      {
        text: label,
        fontSize: 8,
        bold: true,
        color: theme.colours.muted,
        margin: [0, 4, 0, 4]
      },
      {
        text: safeText(value),
        fontSize: 9.5,
        bold: true,
        color: theme.colours.dark,
        alignment: "right",
        margin: [0, 4, 0, 4]
      }
    ];
  }

  function pageHeader(title, subtitle, reportData, theme) {
    return {
      stack: [
        {
          columns: [
            {
              text: "SHEEP MADE SIMPLE",
              fontSize: 8.5,
              bold: true,
              color: theme.colours.green,
              characterSpacing: 1.3
            },
            {
              text: reportData.farm.name,
              fontSize: 8.5,
              bold: true,
              color: theme.colours.muted,
              alignment: "right"
            }
          ],
          margin: [0, 0, 0, 18]
        },
        { text: title, fontSize: 27, bold: true, color: theme.colours.dark },
        subtitle
          ? {
              text: subtitle,
              fontSize: 10,
              color: theme.colours.body,
              margin: [0, 6, 0, 17],
              lineHeight: 1.35
            }
          : null,
        {
          canvas: [
            {
              type: "line",
              x1: 0,
              y1: 0,
              x2: 511,
              y2: 0,
              lineWidth: 1,
              lineColor: theme.colours.line
            }
          ],
          margin: [0, 0, 0, 18]
        }
      ].filter(Boolean)
    };
  }

  function missingMapBox(label, theme) {
    return {
      table: {
        widths: ["*"],
        body: [
          [
            {
              stack: [
                {
                  text: `${label} map not added`,
                  fontSize: 14,
                  bold: true,
                  color: theme.colours.dark
                },
                {
                  text: `Upload a ${label.toLowerCase()} map in My Block to include it in future reports.`,
                  fontSize: 9,
                  color: theme.colours.muted,
                  margin: [0, 6, 0, 0]
                }
              ],
              alignment: "center",
              margin: [20, 30, 20, 30],
              fillColor: theme.colours.cream
            }
          ]
        ]
      },
      layout: "noBorders",
      margin: [0, 22, 0, 0]
    };
  }

  function buildCoverPage(reportData, theme) {
    const map = reportData.maps.overall;
    return {
      stack: [
        {
          text: "SHEEP MADE SIMPLE",
          fontSize: 10,
          bold: true,
          color: theme.colours.green,
          characterSpacing: 1.7,
          margin: [0, 6, 0, 52]
        },
        {
          text: "Farm Management Report",
          fontSize: 34,
          bold: true,
          color: theme.colours.dark,
          margin: [0, 0, 0, 12]
        },
        {
          text: "Your complete sheep management record for your lifestyle block.",
          fontSize: 13,
          color: theme.colours.body,
          lineHeight: 1.35,
          margin: [0, 0, 0, 34]
        },
        {
          text: "PREPARED FOR",
          fontSize: 8,
          bold: true,
          color: theme.colours.muted,
          characterSpacing: 1.1
        },
        {
          text: reportData.farm.name,
          fontSize: 24,
          bold: true,
          color: theme.colours.dark,
          margin: [0, 7, 0, 5]
        },
        {
          text: reportData.farm.location,
          fontSize: 10,
          color: theme.colours.muted,
          margin: [0, 0, 0, 25]
        },
        map
          ? { image: map, fit: [511, 295], alignment: "center", margin: [0, 0, 0, 25] }
          : {
              table: {
                widths: ["*"],
                body: [
                  [
                    {
                      text: "Add an Overall Map in My Block to personalise this cover.",
                      alignment: "center",
                      color: theme.colours.muted,
                      margin: [20, 55, 20, 55],
                      fillColor: theme.colours.cream
                    }
                  ]
                ]
              },
              layout: "noBorders",
              margin: [0, 0, 0, 25]
            },
        {
          columns: [
            {
              stack: [
                { text: "GENERATED", fontSize: 7.5, bold: true, color: theme.colours.muted },
                {
                  text: reportData.meta.generatedDate,
                  fontSize: 10,
                  bold: true,
                  color: theme.colours.dark,
                  margin: [0, 4, 0, 0]
                }
              ]
            },
            {
              stack: [
                {
                  text: "PROFILE STATUS",
                  fontSize: 7.5,
                  bold: true,
                  color: theme.colours.muted
                },
                {
                  text: `${reportData.review.status} - ${reportData.review.percentage}%`,
                  fontSize: 10,
                  bold: true,
                  color: theme.colours.green,
                  margin: [0, 4, 0, 0]
                }
              ],
              alignment: "right"
            }
          ]
        }
      ],
      pageBreak: "after"
    };
  }

  function capacityStatus(reportData) {
    const capacity = numberOr(reportData.flock.capacity.safeYearRound);
    const sheepEq = numberOr(reportData.flock.sheepEquivalents);
    if (!capacity) return "Not assessed";
    if (sheepEq > capacity) return "Above capacity";
    if (sheepEq >= capacity * 0.9) return "Close to capacity";
    return "Comfortable";
  }

  function grazingRotationValue(grazing) {
    const rotation = grazing.currentRotation || {};
    if (numberOr(rotation.days) > 0) {
      return `${formatNumber(rotation.days)} days`;
    }
    if (numberOr(rotation.weeks) > 0) {
      return `${formatNumber(rotation.weeks)} weeks`;
    }
    return "Not calculated";
  }

  function buildExecutiveSummaryPage(reportData, theme) {
    const seasonal = reportData.seasonalPlans;
    const grazing = reportData.grazing;
    const activePaddocks = reportData.paddocks.length;
    const maxShifts =
      grazing.maximumRotation?.maximumShiftCount ||
      grazing.maximumRotation?.shiftCount ||
      activePaddocks;

    return [
      pageHeader(
        "Executive Summary",
        "A one-page overview of your block, flock, grazing system and current management position.",
        reportData,
        theme
      ),
      {
        columns: [
          statCard(
            "Usable grazing area",
            reportData.property.usableGrazingDisplay,
            reportData.farm.location,
            theme
          ),
          statCard(
            "Current flock",
            `${reportData.flock.totalHead} sheep`,
            `${formatNumber(reportData.flock.sheepEquivalents)} sheep equivalents`,
            theme
          ),
          statCard(
            "Normal rotation",
            grazingRotationValue(grazing),
            `${grazing.currentRotation?.grazingShiftCount || activePaddocks} grazing shifts`,
            theme
          )
        ],
        columnGap: 10,
        margin: [0, 0, 0, 10]
      },
      {
        columns: [
          statCard(
            "Safe capacity",
            numberOr(reportData.flock.capacity.safeYearRound) > 0
              ? `${formatNumber(reportData.flock.capacity.safeYearRound)} sheep equivalents`
              : "Not assessed",
            capacityStatus(reportData),
            theme
          ),
          statCard(
            "Active paddocks",
            String(activePaddocks),
            `${maxShifts} possible shifts`,
            theme
          ),
          statCard(
            "Farm profile",
            reportData.review.status,
            `${reportData.review.percentage}% complete`,
            theme
          )
        ],
        columnGap: 10,
        margin: [0, 0, 0, 20]
      },
      sectionTitle("Seasonal Progress", "", theme),
      {
        columns: SEASONS.map(season =>
          statCard(
            seasonal[season].label,
            seasonal[season].status,
            `${seasonal[season].progressPercent}% complete`,
            theme
          )
        ),
        columnGap: 7,
        margin: [0, 0, 0, 20]
      },
      sectionTitle("Current Status", "", theme),
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                stack: [
                  {
                    text: "DRENCHING",
                    fontSize: 8,
                    bold: true,
                    color: theme.colours.muted,
                    characterSpacing: 0.8
                  },
                  {
                    text: reportData.decisions.drench.outcomeName,
                    fontSize: 17,
                    bold: true,
                    color: theme.colours.dark,
                    margin: [0, 5, 0, 8]
                  },
                  {
                    columns: [
                      {
                        text: `Last reviewed: ${reportData.decisions.drench.lastReviewedDisplay}`,
                        fontSize: 9,
                        color: theme.colours.body
                      },
                      {
                        text: `Last drench: ${reportData.decisions.drench.lastDrenchedDisplay}`,
                        fontSize: 9,
                        color: theme.colours.body
                      },
                      {
                        text: `Next review: ${reportData.decisions.drench.nextReviewDisplay}`,
                        fontSize: 9,
                        color: theme.colours.body,
                        alignment: "right"
                      }
                    ]
                  }
                ],
                margin: [13, 12, 13, 12],
                fillColor: theme.colours.blue
              }
            ]
          ]
        },
        layout: "noBorders",
        pageBreak: "after"
      }
    ];
  }

  function buildFarmLayoutPage(reportData, theme) {
    const tableBody = [
      [
        { text: "Paddock", style: "tableHeader" },
        { text: "Size", style: "tableHeader" },
        { text: "Normal stay", style: "tableHeader" },
        { text: "Split?", style: "tableHeader" },
        { text: "Drainage", style: "tableHeader" },
        { text: "Shelter", style: "tableHeader" }
      ],
      ...reportData.paddocks.map(row => [
        row.name,
        row.areaDisplay,
        row.normalDurationDisplay,
        row.canSplit,
        row.drainage,
        row.shelter
      ])
    ];

    return [
      pageHeader(
        "Farm Layout",
        "Your property map and the permanent paddocks that form your normal grazing system.",
        reportData,
        theme
      ),
      reportData.maps.overall
        ? {
            image: reportData.maps.overall,
            fit: [511, 300],
            alignment: "center",
            margin: [0, 0, 0, 20]
          }
        : null,
      {
        columns: [
          statCard("Total property", reportData.property.totalPropertyDisplay, null, theme),
          statCard("Usable grazing", reportData.property.usableGrazingDisplay, null, theme),
          statCard("Active paddocks", String(reportData.paddocks.length), null, theme)
        ],
        columnGap: 10,
        margin: [0, 0, 0, 18]
      },
      { text: "Paddocks", fontSize: 18, bold: true, color: theme.colours.dark, margin: [0, 0, 0, 10] },
      reportData.paddocks.length
        ? {
            table: {
              headerRows: 1,
              widths: ["*", 58, 67, 42, 58, 62],
              body: tableBody
            },
            layout: tableLayout(theme),
            pageBreak: "after"
          }
        : {
            ...missingMapBox("Paddock information", theme),
            pageBreak: "after"
          }
    ].filter(Boolean);
  }

  function buildSeasonalPlanPage(seasonData, reportData, theme) {
    const content = [
      pageHeader(
        `${seasonData.label} Plan`,
        seasonData.subtitle,
        reportData,
        theme
      ),
      {
        columns: [
          statCard("Status", seasonData.status, `${seasonData.progressPercent}% complete`, theme),
          statCard(
            "Last updated",
            seasonData.updatedAt
              ? formatDate(seasonData.updatedAt, "Not updated")
              : safeText(seasonData.updatedLabel, "Not updated").replace(/^Last updated\s*/i, ""),
            null,
            theme
          ),
          statCard(
            seasonData.progressPercent >= 100 ? "Completion date" : "Completed",
            seasonData.completedAt
              ? formatDate(seasonData.completedAt, "Not recorded")
              : seasonData.progressPercent >= 100
                ? "Not recorded"
                : "Not completed",
            null,
            theme
          )
        ],
        columnGap: 10,
        margin: [0, 0, 0, 18]
      },
      {
        table: {
          widths: ["*", 190],
          body: seasonData.rows.map(row => infoRow(row.label, row.value, theme))
        },
        layout: {
          hLineWidth(index, node) {
            return index === 0 || index === node.table.body.length ? 0 : 1;
          },
          hLineColor() {
            return theme.colours.line;
          },
          vLineWidth() {
            return 0;
          },
          paddingLeft() {
            return 10;
          },
          paddingRight() {
            return 10;
          }
        }
      },
      seasonData.map
        ? {
            text: `${seasonData.label} Map`,
            fontSize: 15,
            bold: true,
            color: theme.colours.dark,
            margin: [0, 22, 0, 10]
          }
        : null,
      seasonData.map
        ? { image: seasonData.map, fit: [511, 300], alignment: "center" }
        : missingMapBox(seasonData.label, theme)
    ].filter(Boolean);

    content[0].pageBreak = "before";
    return content;
  }

  function buildCurrentManagementPage(reportData, theme) {
    const drench = reportData.decisions.drench;
    return [
      {
        ...pageHeader(
          "Current Management Status",
          "The latest useful state from your Decision Guides, kept separate from jobs or treatments that were physically completed.",
          reportData,
          theme
        ),
        pageBreak: "before"
      },
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                stack: [
                  {
                    columns: [
                      {
                        text: "DRENCHING",
                        fontSize: 9,
                        bold: true,
                        color: theme.colours.green,
                        characterSpacing: 0.8
                      },
                      {
                        text: drench.hasStatus ? "CURRENT" : "NOT REVIEWED",
                        fontSize: 8,
                        bold: true,
                        color: drench.hasStatus ? theme.colours.green : theme.colours.muted,
                        alignment: "right"
                      }
                    ]
                  },
                  {
                    text: drench.outcomeName,
                    fontSize: 20,
                    bold: true,
                    color: theme.colours.dark,
                    margin: [0, 9, 0, 15]
                  },
                  {
                    table: {
                      widths: ["*", "*"],
                      body: [
                        infoRow("Last reviewed", drench.lastReviewedDisplay, theme),
                        infoRow("Last actual drench", drench.lastDrenchedDisplay, theme),
                        infoRow("Last worm count", drench.lastWormCountDisplay, theme),
                        infoRow("Next review", drench.nextReviewDisplay, theme)
                      ]
                    },
                    layout: {
                      hLineWidth(index, node) {
                        return index === 0 || index === node.table.body.length ? 0 : 1;
                      },
                      hLineColor() {
                        return theme.colours.line;
                      },
                      vLineWidth() {
                        return 0;
                      }
                    }
                  }
                ],
                margin: [16, 15, 16, 15],
                fillColor: theme.colours.blue
              }
            ]
          ]
        },
        layout: "noBorders",
        margin: [0, 0, 0, 18]
      },
      {
        text: "More current-status cards will appear here as additional Decision Guides are connected to My Block.",
        fontSize: 9,
        color: theme.colours.muted,
        italics: true,
        pageBreak: "after"
      }
    ];
  }

  function buildDecisionHistoryPage(reportData, theme) {
    const history = reportData.decisions.history;
    const content = [
      pageHeader(
        "Decision History",
        "The most recent decisions saved from Sheep Made Simple guides.",
        reportData,
        theme
      )
    ];

    if (!history.length) {
      content.push({
        table: {
          widths: ["*"],
          body: [
            [
              {
                stack: [
                  { text: "No decisions saved yet", fontSize: 16, bold: true, color: theme.colours.dark },
                  {
                    text: "Complete a Decision Guide and save the outcome to My Block to begin your timeline.",
                    fontSize: 9,
                    color: theme.colours.muted,
                    margin: [0, 7, 0, 0]
                  }
                ],
                alignment: "center",
                margin: [20, 35, 20, 35],
                fillColor: theme.colours.cream
              }
            ]
          ]
        },
        layout: "noBorders"
      });
    } else {
      history.forEach((record, index) => {
        content.push({
          columns: [
            {
              width: 58,
              stack: [
                {
                  text: record.reviewedDate,
                  fontSize: 8,
                  bold: true,
                  color: theme.colours.green
                }
              ]
            },
            {
              width: 12,
              canvas: [
                { type: "ellipse", x: 5, y: 5, r1: 4, r2: 4, color: theme.colours.green },
                {
                  type: "line",
                  x1: 5,
                  y1: 10,
                  x2: 5,
                  y2: 62,
                  lineWidth: 1,
                  lineColor: index === history.length - 1 ? theme.colours.white : theme.colours.line
                }
              ]
            },
            {
              width: "*",
              stack: [
                { text: record.guideName, fontSize: 14, bold: true, color: theme.colours.dark },
                {
                  text: `Outcome: ${record.outcomeName}`,
                  fontSize: 9.5,
                  bold: true,
                  color: theme.colours.body,
                  margin: [0, 5, 0, 0]
                },
                record.notes
                  ? {
                      text: record.notes,
                      fontSize: 9,
                      color: theme.colours.muted,
                      margin: [0, 5, 0, 0],
                      lineHeight: 1.35
                    }
                  : null,
                record.nextReviewAt
                  ? {
                      text: `Next review: ${record.nextReviewDate}`,
                      fontSize: 8.5,
                      color: theme.colours.muted,
                      margin: [0, 5, 0, 0]
                    }
                  : null
              ].filter(Boolean)
            }
          ],
          columnGap: 7,
          margin: [0, 0, 0, 15],
          unbreakable: true
        });
      });
    }

    content.push({
      text: `Showing the ${history.length} most recent saved decisions.`,
      fontSize: 8,
      color: theme.colours.muted,
      italics: true,
      margin: [0, 8, 0, 0],
      pageBreak: "after"
    });

    return content;
  }

  function buildRecordsPage(reportData, theme) {
    return [
      pageHeader(
        "Farm Records",
        "Key dates for work or treatments physically completed on the farm.",
        reportData,
        theme
      ),
      {
        table: {
          headerRows: 1,
          widths: ["*", "*", "*"],
          body: [
            [
              { text: "Record", style: "tableHeader" },
              { text: "Last done", style: "tableHeader" },
              { text: "Next due", style: "tableHeader" }
            ],
            ...reportData.records.rows.map(row => [
              row.label,
              row.lastDoneDisplay,
              row.nextDueDisplay
            ])
          ]
        },
        layout: tableLayout(theme),
        pageBreak: "after"
      }
    ];
  }

  function buildNotesPage(reportData, theme) {
    return [
      pageHeader(
        "Notes",
        "Observations and information saved in My Block.",
        reportData,
        theme
      ),
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                text: reportData.notes.general,
                fontSize: 11,
                color: theme.colours.body,
                lineHeight: 1.55,
                margin: [15, 15, 15, 15],
                fillColor: "#FBFCFB"
              }
            ]
          ]
        },
        layout: {
          hLineColor() {
            return theme.colours.line;
          },
          vLineColor() {
            return theme.colours.line;
          }
        },
        pageBreak: "after"
      }
    ];
  }

  function buildClosingPage(reportData, theme) {
    const checks = reportData.review.sections;
    const springStatus = reportData.seasonalPlans.spring.status;
    const nextAction =
      springStatus === "Not started"
        ? "Complete your Spring Seasonal Plan"
        : "Keep My Block current as conditions and management decisions change.";

    return [
      pageHeader(
        "About This Report",
        "A personalised snapshot generated from the information saved in Sheep Made Simple.",
        reportData,
        theme
      ),
      {
        text: "Built from experience managing sheep on more than 1,000 New Zealand lifestyle farms.",
        fontSize: 17,
        bold: true,
        color: theme.colours.dark,
        lineHeight: 1.3,
        margin: [0, 0, 0, 22]
      },
      {
        text: "PROFILE COMPLETENESS",
        fontSize: 8,
        bold: true,
        color: theme.colours.green,
        characterSpacing: 1,
        margin: [0, 0, 0, 9]
      },
      {
        table: {
          widths: ["*", "*"],
          body: checks.map(item => [
            {
              text: item.label,
              fontSize: 10,
              bold: true,
              color: theme.colours.dark,
              margin: [8, 8, 8, 8]
            },
            {
              text: item.complete ? "Complete" : "Needs an update",
              fontSize: 9,
              bold: true,
              color: item.complete ? theme.colours.green : theme.colours.muted,
              alignment: "right",
              margin: [8, 8, 8, 8]
            }
          ])
        },
        layout: {
          hLineColor() {
            return theme.colours.line;
          },
          vLineWidth() {
            return 0;
          }
        }
      },
      {
        columns: [
          statCard(
            "Profile status",
            reportData.review.status,
            `${reportData.review.percentage}% complete`,
            theme
          ),
          statCard(
            "Generated",
            reportData.meta.generatedDate,
            reportData.meta.generatedTime,
            theme
          )
        ],
        columnGap: 10,
        margin: [0, 22, 0, 20]
      },
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                stack: [
                  {
                    text: "NEXT RECOMMENDED ACTION",
                    fontSize: 8,
                    bold: true,
                    color: theme.colours.green,
                    characterSpacing: 0.8
                  },
                  {
                    text: nextAction,
                    fontSize: 15,
                    bold: true,
                    color: theme.colours.dark,
                    margin: [0, 7, 0, 5]
                  },
                  {
                    text: "A current profile helps Sheep Made Simple provide more useful seasonal guidance and recommendations.",
                    fontSize: 9,
                    color: theme.colours.body,
                    lineHeight: 1.4
                  }
                ],
                margin: [15, 14, 15, 14],
                fillColor: theme.colours.blue
              }
            ]
          ]
        },
        layout: "noBorders"
      }
    ];
  }

  function buildFarmManagementDefinition(reportData, options = {}) {
    if (!isObject(reportData)) {
      throw new Error("A prepared Farm Management Report data model is required.");
    }

    const theme = options.theme || REPORT_THEME;
    const content = [];

    content.push(buildCoverPage(reportData, theme));
    content.push(...buildExecutiveSummaryPage(reportData, theme));
    content.push(...buildFarmLayoutPage(reportData, theme));

    for (const season of SEASONS) {
      content.push(...buildSeasonalPlanPage(reportData.seasonalPlans[season], reportData, theme));
    }

    content.push(...buildCurrentManagementPage(reportData, theme));
    content.push(...buildDecisionHistoryPage(reportData, theme));
    content.push(...buildRecordsPage(reportData, theme));
    content.push(...buildNotesPage(reportData, theme));
    content.push(...buildClosingPage(reportData, theme));

    return {
      pageSize: theme.page.size,
      pageMargins: theme.page.margins,
      content,
      defaultStyle: {
        font: "Roboto",
        fontSize: 9,
        color: theme.colours.body
      },
      styles: {
        tableHeader: {
          fontSize: 8,
          bold: true,
          color: theme.colours.white,
          alignment: "left"
        },
        label: {
          fontSize: 8,
          bold: true,
          color: theme.colours.muted
        },
        value: {
          fontSize: 13,
          bold: true,
          color: theme.colours.dark
        }
      },
      footer(page, pages) {
        if (page === 1) return null;
        return {
          margin: [42, 0, 42, 16],
          columns: [
            {
              text: `SHEEP MADE SIMPLE  |  ${reportData.farm.name}`,
              fontSize: 7.5,
              color: theme.colours.muted
            },
            {
              text: `Page ${page} of ${pages}`,
              fontSize: 7.5,
              color: theme.colours.muted,
              alignment: "right"
            }
          ]
        };
      },
      info: {
        title: `${reportData.farm.name} - Farm Management Report`,
        author: "Sheep Made Simple",
        subject: "Lifestyle sheep farm management report"
      }
    };
  }

  function createPdfDownload(pdfMake, definition, filename) {
    return new Promise((resolve, reject) => {
      try {
        const pdf = pdfMake.createPdf(definition);
        pdf.download(filename, () => resolve());
      } catch (error) {
        reject(error);
      }
    });
  }

  async function init(options = {}) {
    engineOptions = { ...engineOptions, ...options };

    if (ready && pdfDependenciesReady && hasConfiguredPdfFonts()) {
      return api;
    }

    if (initialisationPromise) {
      return initialisationPromise;
    }

    initialisationPromise = (async () => {
      requireStorage();

      if (options.requirePdf !== false) {
        await ensurePdfDependencies();
      }

      ready = true;
      log(`Ready. Version ${VERSION}.`);
      dispatch("sms:report-engine-ready", { version: VERSION });
      return api;
    })().catch(error => {
      initialisationPromise = null;
      ready = false;
      throw error;
    });

    return initialisationPromise;
  }

  function isReady() {
    return ready;
  }

  async function generateFarmManagementReport(options = {}) {
    if (generationPromise) return generationPromise;

    generationPromise = (async () => {
      let filename = "";
      dispatch("sms:report-generation-started", { filename: null });

      try {
        await init({ ...engineOptions, ...options, requirePdf: true });
        const pdfMake = await ensurePdfDependencies();
        const reportData = await getFarmReportData(options);
        const definition = buildFarmManagementDefinition(reportData, options);
        filename = buildFilename(reportData, options);
        await createPdfDownload(pdfMake, definition, filename);

        const result = {
          success: true,
          filename,
          reportData,
          definition
        };

        dispatch("sms:report-generation-completed", { filename });
        return result;
      } catch (error) {
        console.error(LOG_PREFIX, "Farm Management Report generation failed.", error);
        dispatch("sms:report-generation-failed", {
          filename: filename || null,
          error
        });

        if (
          error?.message === "SMSReportEngine requires SMSFarmStorage." ||
          error?.message === "Could not load pdfMake." ||
          error?.message === "Could not load the PDF font library." ||
          error?.message === "The PDF generator is unavailable." ||
          error?.message === "The PDF font library is unavailable." ||
          error?.message.includes("Shopify asset location") ||
          error?.message.includes("Shopify asset URL")
        ) {
          throw error;
        }

        throw new Error("The Farm Management Report could not be generated.", {
          cause: error
        });
      } finally {
        generationPromise = null;
      }
    })();

    return generationPromise;
  }

  const api = Object.freeze({
    version: VERSION,
    init,
    isReady,
    getFarmReportData,
    buildFarmManagementDefinition,
    generateFarmManagementReport
  });

  return api;
})();
